import {
  ApiError,
  getCommission,
  getExecution,
  isMissingRoute,
  listClips,
  type Clip,
  type CommissionDetail,
  type ExecutionDetail,
  type Failure,
} from '../api';
import { useAsync } from '../lib/async';
import { DangerAction } from '../lib/danger';
import { FailureBlock } from '../lib/failure';
import { InlinePlayer } from '../lib/media';
import {
  Empty,
  Facts,
  Id,
  Link,
  NotBuilt,
  Pill,
  Result,
  StatusPill,
  Toolbar,
  elapsed,
  money,
  span,
  when,
} from '../lib/ui';

/**
 * One commission, from both sources of truth at once.
 *
 * The two halves are fetched separately and labelled separately, because they
 * demonstrably disagree and blending them would hide the disagreement:
 *
 *   - `GET /admin/commissions/{id}` is the entity — request, money, shot plan,
 *     video, ledger. It asks the orchestrator nothing, by design.
 *   - `GET /admin/pipeline/executions/{id}` is the run — where it got to, and
 *     why it stopped. It 404s for the eight commissions that never had an
 *     execution, and for anything past the 90-day retention or a state-machine
 *     rebuild.
 *
 * The failure story is the reason this screen exists. The one failed episode in
 * production is a **SUCCEEDED** execution: the `Release the hold` catcher ran
 * and returned cleanly, so the run is satisfied while the commission says
 * `failed`. Filtering executions on FAILED finds none of them. The API reports
 * the cause with a `source` — "execution", "caught" or "history" — and that word
 * is shown, because it is the difference between "the run died" and "the run
 * finished tidying up after something that died".
 */
export function Commission({ commissionId }: { commissionId: string }) {
  const { result, reload } = useAsync(() => getCommission(commissionId), [commissionId]);

  return (
    <>
      <Toolbar>
        <Link to={{ name: 'pipeline' }}>‹ all commissions</Link>
        <span className="spacer" />
        <code className="small">{commissionId}</code>
        <button onClick={reload}>Refresh</button>
      </Toolbar>

      <Result of={result} what="this commission" retry={reload}>
        {(detail) => (
          <>
            <Entity detail={detail} />
            <Execution commissionId={commissionId} />
            <Shots detail={detail} />
            <Generated commissionId={commissionId} />
            <Money detail={detail} />
            <Stop detail={detail} />
          </>
        )}
      </Result>
    </>
  );
}

function Entity({ detail }: { detail: CommissionDetail }) {
  return (
    <section className="detail">
      <h2>
        <StatusPill status={detail.status} />{' '}
        {detail.kind === 'preview' ? <Pill>free peek</Pill> : <Pill>episode</Pill>}
        {detail.orphaned && (
          <>
            {' '}
            <Pill tone="bad" title="no familyId or no createdAt: absent from every byFamily index, so only a scan finds it">
              orphaned row
            </Pill>
          </>
        )}
      </h2>

      <Facts
        rows={[
          ['Request', detail.request],
          [
            'Family',
            detail.familyId ? (
              <Link to={{ name: 'families', familyId: detail.familyId }}>
                {detail.familyName || detail.familyId}
              </Link>
            ) : (
              <span className="warn">
                none — this row is invisible to the family's own list too
              </span>
            ),
          ],
          [
            'Character',
            detail.character ? (
              <>
                {detail.character.characterName || 'unnamed'}{' '}
                <Id value={detail.character.characterId} />
              </>
            ) : detail.characterId ? (
              <>
                <Id value={detail.characterId} chars={12} />{' '}
                <span className="warn small">no character row</span>
              </>
            ) : null,
          ],
          ['Created', when(detail.createdAt)],
          ['Approved', when(detail.approvedAt)],
          ['Preview at', when(detail.previewAt)],
          [
            'Credits',
            detail.creditsWaived ? (
              <span title="an unlimited family: no credits moved, real provider money did">
                {detail.creditsWaived} waived
              </span>
            ) : (
              detail.creditsHeld
            ),
          ],
          [
            'Video',
            detail.video ? (
              <>
                {detail.video.title || detail.video.videoId}{' '}
                <StatusPill status={detail.video.status} />{' '}
                {detail.video.stitched ? <Pill tone="on">stitched</Pill> : <Pill>playlist</Pill>}{' '}
                <span className="muted small">{detail.video.segments} segments</span>
              </>
            ) : null,
          ],
        ]}
      />

      {detail.failureReason && (
        <>
          {/* "according to the commission row", not according to a database.
              The contrast this heading is drawing is with the RUN's own account
              of itself further down — the row and the run demonstrably disagree
              — and that contrast holds on any lane. Naming the store instead
              meant renaming this heading when the store moved, which is exactly
              what did not happen: it still said DynamoDB with Cosmos behind it. */}
          <h3>Why it failed, according to the commission row</h3>
          <FailureBlock reason={detail.failureReason} />
        </>
      )}
    </section>
  );
}

/**
 * The run — the pipeline's half of the story.
 *
 * A 404 FROM THE HANDLER is the normal case for eight of the twenty live
 * commissions and is NOT an error: peeks never reach the pipeline at all, and
 * executions age out of retention or vanish with a rebuilt state machine. The
 * handler returns a full explanation in `detail`, so that is rendered as a fact.
 *
 * A 404 FROM THE HOST IS A DIFFERENT STATEMENT and this used to conflate them.
 * `err.status === 404` alone caught both, so an unregistered path — which on
 * Azure answers a BODYLESS Kestrel 404 whose entire message is the string "404"
 * — made `why` the characters "404" and put the screen's own sentence above it:
 * "No execution for this commission." That is a claim about DATA, asserted
 * confidently, when the truth was "this route is not deployed". It also made the
 * `missing=` prop below unreachable: nothing could ever get past this catch to
 * let Result classify it, so the one message that named the real cause was dead
 * code sitting three lines away.
 *
 * `isMissingRoute` is the same discriminator every other screen uses — the
 * handler's literal "unknown pipeline route" body, or a 404 with no handler JSON
 * in it at all.
 */
function Execution({ commissionId }: { commissionId: string }) {
  const { result, reload } = useAsync(async () => {
    try {
      return { found: true as const, execution: await getExecution(commissionId) };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404 && !isMissingRoute(err)) {
        const body = err.body as { detail?: string } | null;
        return { found: false as const, why: body?.detail ?? err.message };
      }
      throw err;
    }
  }, [commissionId]);

  return (
    <section className="detail">
      <h2>The run — according to the orchestrator</h2>
      <Result
        of={result}
        what="the execution"
        retry={reload}
        missing="This build of the staff API has no /admin/pipeline routes."
        unavailableDetail="Everything else on this page — the commission row, its shots, its clips and what it cost — is a database read and is still true. Only the run itself is invisible."
      >
        {(found) =>
          !found.found ? (
            <div className="notice">
              <b>No execution for this commission.</b>
              <span className="muted small">{found.why}</span>
            </div>
          ) : (
            <ExecutionBody execution={found.execution} />
          )
        }
      </Result>
    </section>
  );
}

function ExecutionBody({ execution }: { execution: ExecutionDetail }) {
  return (
    <>
      <Facts
        rows={[
          ['Execution status', <StatusPill status={execution.status} />],
          [
            'Ran for',
            execution.durationSeconds
              ? span(execution.durationSeconds)
              : elapsed(execution.startDate, execution.stopDate),
          ],
          ['Started', when(execution.startDate)],
          ['Stopped', when(execution.stopDate)],
          [
            'Redrives',
            execution.redriveCount ? (
              <span className="warn">
                {execution.redriveCount} — nothing in this repo calls
                any redrive API, so somebody did this by hand. A redrive
                regenerates every shot with no hold taken.
              </span>
            ) : (
              0
            ),
          ],
          [
            'Now in',
            execution.trail?.currentState ?? (
              <span className="muted">no state recorded in the tail</span>
            ),
          ],
        ]}
      />

      {execution.failure && <FailureCause failure={execution.failure} />}

      {execution.trail?.error && (
        <div className="notice notice-warn">
          <b>The event tail could not be read.</b>
          <span className="mono small">{execution.trail.error}</span>
          <span className="muted small">
            Everything above came from describe_execution and is still true.
          </span>
        </div>
      )}

      {execution.trail?.states?.length ? (
        <>
          <h3>Where it went, newest first</h3>
          <p>
            {execution.trail.states.map((state, index) => (
              <span key={`${state}-${index}`}>
                {index > 0 && <span className="muted"> ← </span>}
                <Pill tone={index === 0 ? 'run' : 'plain'}>{state}</Pill>
              </span>
            ))}
          </p>
          <p className="muted small">
            {execution.trail.eventsRead} events read from the end.{' '}
            {execution.trail.truncated
              ? 'There is OLDER history before this window — a failure early in a long run can be off the end of this tail.'
              : 'That is the whole history.'}{' '}
            The full history is never fetched: a 450-shot episode runs to
            thousands of events.
          </p>
        </>
      ) : null}

      {execution.input && (
        <>
          <h3>What the pipeline was asked for</h3>
          <p className="muted small">
            Straight from the execution. For an execution whose commission row has
            been deleted, this is the only surviving record of whose it was.
          </p>
          <pre className="raw">{JSON.stringify(execution.input, null, 2)}</pre>
        </>
      )}

    </>
  );
}

function FailureCause({ failure }: { failure: Failure }) {
  const explained =
    failure.source === 'caught'
      ? 'The execution SUCCEEDED because the catch-all handler ran and returned cleanly. The commission still failed. This is what every real pipeline failure in this system looks like.'
      : failure.source === 'history'
        ? 'Recovered from the reverse event tail — describe_execution had nothing, so this came from the history.'
        : 'The execution itself failed.';
  return (
    <>
      <h3>Cause of death</h3>
      <div className="notice notice-bad">
        <b>{failure.error || 'failed'}</b>
        <span className="muted small">{explained}</span>
        {failure.state && <span className="small">in state {failure.state}</span>}
        {failure.at && <span className="muted small">{when(failure.at)}</span>}
      </div>
      {failure.cause && <FailureBlock reason={failure.cause} />}
    </>
  );
}

function Shots({ detail }: { detail: CommissionDetail }) {
  const shots = detail.shots ?? [];
  return (
    <section className="detail">
      <h2>The shot plan</h2>
      {detail.shotStatusNote && (
        <div className="notice notice-warn">
          <b>These are planned shots, not progress.</b>
          <span className="muted small">{detail.shotStatusNote}</span>
          <span className="muted small">
            So a progress bar here would be a lie. What each shot actually
            produced is in "What it generated" below, keyed on the asset rows.
          </span>
        </div>
      )}
      {!shots.length ? (
        <Empty what="shots" />
      ) : (
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Canonical</th>
              <th>Character</th>
              <th>Reuse</th>
              <th>Tier</th>
              <th className="num">Secs</th>
            </tr>
          </thead>
          <tbody>
            {shots.map((shot) => (
              <tr key={shot.shotIndex ?? shot.shotId}>
                <td className="mono">{shot.shotIndex}</td>
                <td>
                  <StatusPill status={shot.status} />
                </td>
                <td>
                  <code className="small">{shot.canonical}</code>
                  {shot.description && (
                    <div className="muted small clamp" title={shot.description}>
                      {shot.description}
                    </div>
                  )}
                </td>
                <td className="muted small">{shot.characterName}</td>
                <td className="muted small">{shot.reuse}</td>
                <td className="muted small">{shot.qualityTier}</td>
                <td className="num muted">{shot.durationSeconds}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detail.shotsTruncated && (
        <p className="warn small">
          The shot plan was truncated at the server's cap — there are more shots
          than are shown here.
        </p>
      )}
    </section>
  );
}

/**
 * What this commission actually put in the pool, and what it cost.
 *
 * This is the numerator the shot table does not have. `showling-assets` carries
 * `commissionId` and `shotId` on generated clips, and `/admin/clips?commission=`
 * filters on it — a filtered scan, because the only index on that table
 * partitions on the tag signature. It is also where a human can watch the output,
 * which given there is no video moderation anywhere in this system is the only
 * review that exists.
 */
function Generated({ commissionId }: { commissionId: string }) {
  const { result, reload } = useAsync(
    () => listClips({ commission: commissionId, limit: 100 }),
    [commissionId],
  );
  return (
    <section className="detail">
      <h2>What it generated</h2>
      <Result of={result} what="the assets from this commission" retry={reload}>
        {(page) =>
          !page.rows.length ? (
            <>
              <Empty what="assets from this commission" />
              <p className="muted small">
                Provenance (`commissionId` / `shotId`) is written on 10 of 133
                asset rows, so an older commission can legitimately have produced
                assets that carry no back-reference. Absence here is not proof
                nothing was generated.
              </p>
            </>
          ) : (
            <div className="split">
              {page.rows.map((clip) => (
                <ClipCard key={clip.assetId} clip={clip} />
              ))}
            </div>
          )
        }
      </Result>
    </section>
  );
}

function ClipCard({ clip }: { clip: Clip }) {
  return (
    <div>
      <Facts
        rows={[
          ['Shot', clip.shotId],
          ['Canonical', <code className="small">{clip.canonical}</code>],
          ['Cost', money(clip.costUsd)],
          ['Scope', clip.reuse],
        ]}
      />
      <InlinePlayer
        assetId={clip.assetId}
        {...(clip.storageKey ? { storageKey: clip.storageKey } : {})}
        familyScoped={Boolean(clip.familyId)}
      />
      <p className="small">
        <Link to={{ name: 'clips', assetId: clip.assetId }}>everything about this asset ›</Link>
      </p>
    </div>
  );
}

function Money({ detail }: { detail: CommissionDetail }) {
  const rows = detail.ledger ?? [];
  return (
    <section className="detail">
      <h2>The money</h2>
      {!rows.length ? (
        <>
          <Empty what="ledger entries for this commission" />
          {detail.kind === 'preview' && (
            <p className="muted small">
              Expected for a peek: the preview path writes no ledger row at all,
              and on an unlimited family it spends no preview token either. Real
              provider money moves and nothing records it against this family.
            </p>
          )}
        </>
      ) : (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Type</th>
              <th className="num">Credits</th>
              <th>Why</th>
              <th>Who</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => (
              <tr key={entry.entryId}>
                <td className="muted nowrap">{when(entry.at)}</td>
                <td>
                  {entry.type}
                  {entry.waived && <Pill> waived</Pill>}
                </td>
                <td className={`num ${entry.credits < 0 ? 'down' : 'up'}`}>
                  {entry.waived && entry.wouldHaveCost
                    ? money(entry.wouldHaveCost)
                    : entry.credits}
                </td>
                <td className="muted">{entry.reason}</td>
                <td className="muted">{entry.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Stopping a running commission — designed, wired, and deliberately not live.
 *
 * The control is here rather than absent because "no button" and "a button that
 * does nothing" are both worse than a button that says why it is off.
 *
 * Two reasons it is off, and the first is a correctness one that is easy to get
 * wrong: `StopExecution` on its own is worse than useless. `Release the hold` is
 * a `Catch` on `States.ALL`, and **an ABORTED execution does not run catchers**,
 * so aborting strands the family's credit hold permanently — they are charged
 * for an episode that will never exist, and `admin_pipeline.py` will then flag
 * it as `finished-but-open` forever. A stop route has to call `release_hold`
 * itself, in the same handler, and no such route is deployed.
 *
 * The second is posture: staff sign-in is whatever the tenant enforces, which
 * this product does not control. Adding the ability to kill a
 * family's in-flight episode on top of a password-only account makes the
 * highest-priority gap on this surface worse. The console is read-only until
 * that lands.
 */
function Stop({ detail }: { detail: CommissionDetail }) {
  if (detail.status !== 'generating') return null;
  return (
    <section className="detail">
      <h2>Stop this commission</h2>
      <NotBuilt
        what="Stopping a running commission"
        detail={
          <>
            No route exists, and it cannot be a bare terminate: killing an
            orchestration from outside skips the handler that gives the
            credits back, so stopping
            would leave {detail.creditsHeld ?? 'the'} credits held against a film
            that will never exist. The handler has to refund in the same call.
            It would need a typed commissionId and a recorded reason either way.
          </>
        }
      />
      {/* The pattern, kept wired so the day the route lands the control is
          already correct rather than improvised: types the id, names the family
          and the money, requires a reason, and is never the default button. */}
      <DangerAction
        label="Stop and refund"
        title={`Stop commission ${detail.commissionId}`}
        confirmValue={detail.commissionId}
        confirmLabel="Type the commission id to confirm"
        facts={[
          ['Family', detail.familyName || detail.familyId || 'unknown'],
          ['Request', detail.request ?? '—'],
          ['Credits held', String(detail.creditsHeld ?? '—')],
          ['Running for', elapsed(detail.createdAt)],
        ]}
        consequences="The execution is aborted and the credit hold is released in the same call. The family keeps their credits and loses the film."
        disabled
        disabledNote="POST /admin/commissions/{id}/stop is not deployed."
        onConfirm={async () => {
          throw new Error('not deployed');
        }}
      />
    </section>
  );
}

/* There used to be a `consoleUrl()` here that built a deep link into the Step
   Functions console from the execution ARN. It is gone with the AWS account it
   pointed at: the link would have opened a sign-in page for an account that no
   longer exists, which is worse than no link, because an operator would spend
   the incident trying to get into it. The event tail above is now the whole
   record — see the note it prints about older history being off the end. */
